import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, X, Check, Clock, Upload, Trash2, Loader2, FileText,
  House, Building2, Trees, CircleHelp, Compass, Users, ClipboardList, HardHat,
} from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl } from "@/lib/api";
import { Logo } from "@/components/Logo";

const TOTAL = 9;

/* ------------------------------ données ------------------------------ */

const PROJECT_TYPES = [
  { id: "Rénovation", sub: "Transformer ou moderniser un logement existant.", img: "/api/static/models/bastide-int2.webp" },
  { id: "Aménagement intérieur", sub: "Repenser vos espaces et votre façon de les vivre.", img: "/api/static/models/atrium-int1.webp" },
  { id: "Extension & transformation", sub: "Agrandir ou faire évoluer votre maison.", img: "/api/static/models/clos.webp" },
  { id: "Construction", sub: "Imaginer et construire une maison qui vous ressemble.", img: "/api/static/models/ecrin.webp" },
];

const PROPERTY_TYPES = [
  { id: "Maison", icon: House, label: "Maison" },
  { id: "Appartement", icon: Building2, label: "Appartement" },
  { id: "Terrain / construction", icon: Trees, label: <>Terrain /<br />Construction</> },
  { id: "Autre", icon: CircleHelp, label: "Autre" },
];

const SURFACES = ["Moins de 50 m²", "50–80 m²", "80–120 m²", "120–160 m²", "Plus de 160 m²", "Je ne sais pas"];

const WORK_OPTIONS = {
  "Rénovation": [
    "Cuisine", "Salle de bains", "Pièces de vie", "Chambres",
    "Isolation / énergie", "Rénovation complète", "Redistribution des espaces", "Autre",
  ],
  "Aménagement intérieur": [
    "Cuisine", "Salle de bains", "Pièces de vie", "Chambres",
    "Isolation / énergie", "Rénovation complète", "Redistribution des espaces", "Autre",
  ],
  "Extension & transformation": [
    "Agrandir la pièce de vie", "Créer une chambre", "Créer une suite parentale",
    "Ajouter un étage", "Créer un nouvel espace", "Autre",
  ],
  "Construction": [
    "Maison compacte", "Maison familiale", "Plain-pied",
    "Maison à étage", "Projet personnalisé", "Je ne sais pas encore",
  ],
};

const AVANCEMENTS = [
  { id: "J'ai une idée", sub: "Je sais globalement ce que je souhaite réaliser." },
  { id: "Je commence à me renseigner", sub: "J'explore encore les possibilités." },
  { id: "J'ai déjà des plans ou des devis", sub: "Le projet commence à être défini." },
  { id: "Je souhaite démarrer prochainement", sub: "Je cherche les bons professionnels." },
  { id: "Mon projet est déjà engagé", sub: "Les travaux ou les démarches sont en cours." },
];

const BUDGETS = [
  "Je ne sais pas encore", "Moins de 30 000 €", "30 000 à 60 000 €", "60 000 à 100 000 €",
  "100 000 à 150 000 €", "150 000 à 250 000 €", "Plus de 250 000 €",
];

const TIMELINES = [
  "Dès que possible", "Dans les 3 mois", "Dans 3 à 6 mois",
  "Dans 6 à 12 mois", "Plus tard", "Je ne sais pas encore",
];

const NEEDS = [
  { id: "Structurer mon projet", sub: "Clarifier les besoins, les priorités et les prochaines étapes.", icon: Compass },
  { id: "Trouver les bons professionnels", sub: "Identifier les professionnels adaptés au projet.", icon: Users },
  { id: "Préparer les plans et les démarches", sub: "Organiser les éléments nécessaires avant les travaux.", icon: ClipboardList },
  { id: "Être accompagné pendant les travaux", sub: "Garder une vision claire de l'avancement du projet.", icon: HardHat },
  { id: "Je ne sais pas encore", sub: "CozyHome m'aide à déterminer par où commencer.", icon: CircleHelp },
];

const INITIAL = {
  project_type: "", property_type: "", surface_range: "", work_types: [],
  avancement: "", timeline: "", budget: "", postal_code: "", city: "", insee_code: "",
  needs: [], details: "", firstname: "", lastname: "", email: "", phone: "",
  consent: false, marketing: false,
  // Conservés pour la compatibilité avec le backend existant
  priorities: [], accompaniment: "",
};

const COVERED_MESSAGE =
  "Votre projet se situe dans une zone couverte par CozyHome. Votre demande va être transmise à votre interlocuteur local. Vous recevrez ses coordonnées dès la prise en charge de votre dossier.";
const UNCOVERED_MESSAGE =
  "CozyHome n'est pas encore directement présent dans votre secteur. Nous allons vérifier si l'un de nos partenaires situés à proximité peut prendre en charge votre projet. Nous revenons vers vous rapidement.";

/* --------------------------- petits composants --------------------------- */

const Kicker = ({ children }) => (
  <p className="text-xs font-bold uppercase tracking-[0.28em] text-cognac">{children}</p>
);

const StepTitle = ({ children }) => (
  <h1 className="mt-4 font-display text-3xl font-medium leading-tight tracking-tight text-navy sm:text-5xl">
    {children}
  </h1>
);

const StepSub = ({ children }) => (
  <p className="mt-3 text-sm leading-relaxed text-stone-600 sm:text-base">{children}</p>
);

const ImageCard = ({ selected, onClick, img, title, sub, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    aria-pressed={selected}
    className={`group overflow-hidden rounded-2xl border-2 bg-white text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cognac ${
      selected ? "border-cognac shadow-sm" : "border-[#EDEAE3] hover:border-cognac/50"
    }`}
  >
    <span className="block h-28 w-full overflow-hidden sm:h-32">
      <img
        src={assetUrl(img)}
        alt=""
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
    </span>
    <span className="flex items-start justify-between gap-3 px-5 py-4">
      <span className="min-w-0">
        <span className="block text-sm font-bold text-navy sm:text-base">{title}</span>
        <span className="mt-0.5 block text-xs leading-relaxed text-stone-500 sm:text-sm">{sub}</span>
      </span>
      <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200 ${
        selected ? "border-cognac bg-cognac text-white" : "border-stone-200 text-transparent"
      }`}>
        <Check className="h-3.5 w-3.5" />
      </span>
    </span>
  </button>
);

const OptionCard = ({ selected, onClick, icon: Icon, title, sub, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    aria-pressed={selected}
    className={`group flex w-full items-center gap-4 rounded-2xl border-2 bg-white px-5 py-4 text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cognac sm:px-6 sm:py-5 ${
      selected
        ? "border-cognac bg-cognac/5 shadow-sm"
        : "border-[#EDEAE3] hover:border-cognac/50 hover:bg-cream/60"
    }`}
  >
    {Icon && (
      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors duration-200 ${
        selected ? "bg-cognac text-white" : "bg-sage/10 text-sage"
      }`}>
        <Icon className="h-5 w-5" />
      </span>
    )}
    <span className="min-w-0 flex-1">
      <span className={`block text-sm font-bold sm:text-base ${selected ? "text-navy" : "text-navy/85"}`}>{title}</span>
      {sub && <span className="mt-0.5 block text-xs leading-relaxed text-stone-500 sm:text-sm">{sub}</span>}
    </span>
    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200 ${
      selected ? "border-cognac bg-cognac text-white" : "border-stone-200 text-transparent"
    }`}>
      <Check className="h-3.5 w-3.5" />
    </span>
  </button>
);

const LocationTypeCard = ({ selected, onClick, icon: Icon, label, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    aria-pressed={selected}
    className={`flex h-full w-full flex-col rounded-2xl border-2 bg-white px-3.5 py-3.5 text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cognac ${
      selected
        ? "border-cognac bg-cognac/5 shadow-sm"
        : "border-[#EDEAE3] hover:border-cognac/50 hover:bg-cream/60"
    }`}
  >
    <span className="flex items-center justify-between">
      {Icon && (
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors duration-200 ${
          selected ? "bg-cognac text-white" : "bg-sage/10 text-sage"
        }`}>
          <Icon className="h-4 w-4" />
        </span>
      )}
      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200 ${
        selected ? "border-cognac bg-cognac text-white" : "border-stone-200 text-transparent"
      }`}>
        <Check className="h-3 w-3" />
      </span>
    </span>
    <span className={`mt-3 block text-sm font-bold leading-snug ${selected ? "text-navy" : "text-navy/85"}`}>{label}</span>
  </button>
);


const Pill = ({ selected, onClick, label, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    aria-pressed={selected}
    className={`rounded-full border-2 px-5 py-3 text-sm font-bold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cognac ${
      selected
        ? "border-cognac bg-cognac text-white"
        : "border-[#EDEAE3] bg-white text-navy hover:border-cognac/50"
    }`}
  >
    {label}
  </button>
);

const ErrorMsg = ({ children }) => (
  <motion.p
    data-testid="estimate-error"
    initial={{ opacity: 0, y: -6 }}
    animate={{ opacity: 1, y: 0 }}
    className="mt-5 rounded-xl bg-cognac/10 px-4 py-3 text-sm font-semibold text-cognac-dark"
  >
    {children}
  </motion.p>
);

const inputClass =
  "w-full rounded-2xl border-2 border-[#EDEAE3] bg-white px-5 py-3.5 text-base font-semibold text-navy placeholder:font-normal placeholder:text-stone-400 focus:border-cognac focus:outline-none";

/* --------------------------------- page --------------------------------- */

export default function EstimerProjet() {
  const location = useLocation();
  const [step, setStep] = useState(0); // 0 = intro, 1..9 = questions, 10 = confirmation
  const [a, setA] = useState(() => {
    const init = { ...INITIAL };
    const pre = location.state?.accompaniment;
    if (pre === "support") init.accompaniment = "Je souhaite surtout être conseillé";
    if (pre === "expertise") init.accompaniment = "Je souhaite déléguer davantage";
    return init;
  });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [routingCovered, setRoutingCovered] = useState(null);
  const [communeMatches, setCommuneMatches] = useState([]);
  const [communeStatus, setCommuneStatus] = useState("idle"); // idle | loading | single | multi | none
  const submittingRef = useRef(false);
  const fileRef = useRef(null);
  const topRef = useRef(null);

  const set = (key, value) => {
    setA((prev) => {
      const nextState = { ...prev, [key]: value };
      // Changer de type de projet réinitialise les envies (listes différentes)
      if (key === "project_type" && value !== prev.project_type) nextState.work_types = [];
      return nextState;
    });
    setError("");
  };

  const toggleMulti = (key, value) => {
    setA((prev) => {
      const list = prev[key];
      if (list.includes(value)) return { ...prev, [key]: list.filter((v) => v !== value) };
      return { ...prev, [key]: [...list, value] };
    });
    setError("");
  };

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: "instant", block: "start" });
    window.scrollTo(0, 0);
  }, [step]);

  // Recherche automatique de la commune dès que le code postal atteint 5 chiffres.
  useEffect(() => {
    const cp = a.postal_code.trim();
    if (!/^\d{5}$/.test(cp)) {
      setCommuneMatches([]);
      setCommuneStatus("idle");
      return;
    }
    let active = true;
    setCommuneStatus("loading");
    api.get("/communes/search", { params: { cp } })
      .then((res) => {
        if (!active) return;
        const matches = res.data || [];
        setCommuneMatches(matches);
        if (matches.length === 1) {
          setCommuneStatus("single");
          setA((prev) => ({ ...prev, city: matches[0].name, insee_code: matches[0].insee_code }));
        } else if (matches.length > 1) {
          setCommuneStatus("multi");
          setA((prev) => (matches.some((m) => m.insee_code === prev.insee_code) ? prev : { ...prev, city: "", insee_code: "" }));
        } else {
          setCommuneStatus("none");
          setA((prev) => ({ ...prev, insee_code: "" }));
        }
      })
      .catch(() => { if (active) setCommuneStatus("none"); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a.postal_code]);

  const validate = () => {
    switch (step) {
      case 1: return a.project_type ? "" : "Sélectionnez une réponse pour continuer.";
      case 2:
        if (!a.property_type) return "Sélectionnez le type de lieu pour continuer.";
        if (!a.surface_range) return "Sélectionnez une surface approximative pour continuer.";
        return "";
      case 3: return a.work_types.length ? "" : "Sélectionnez au moins une réponse pour continuer.";
      case 4: return a.avancement ? "" : "Sélectionnez une réponse pour continuer.";
      case 5: return a.budget ? "" : "Sélectionnez une réponse pour continuer.";
      case 6: return a.timeline ? "" : "Sélectionnez une réponse pour continuer.";
      case 7:
        if (!/^\d{5}$/.test(a.postal_code.trim())) return "Indiquez le code postal du projet (5 chiffres, ex. 70000).";
        if (communeStatus === "multi" && !a.insee_code) return "Sélectionnez votre commune dans la liste.";
        if (a.city.trim().length < 2) return "Indiquez la commune de votre projet.";
        return "";
      case 8: return a.needs.length ? "" : "Sélectionnez au moins une réponse pour continuer.";
      case 9:
        if (!a.firstname.trim() || !a.lastname.trim()) return "Renseignez votre prénom et votre nom.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email.trim())) return "Indiquez une adresse email valide.";
        if (a.phone.replace(/\D/g, "").length < 8) return "Indiquez un numéro de téléphone valide.";
        if (!a.consent) return "Cochez la case de confidentialité pour envoyer votre demande.";
        return "";
      default: return "";
    }
  };

  const next = () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError("");
    setStep((s) => Math.min(s + 1, TOTAL + 1));
  };

  const back = () => {
    setError("");
    setStep((s) => Math.max(s - 1, 0));
  };

  // Navigation clavier : Entrée = Continuer (hors zone de texte)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Enter" || e.target.tagName === "TEXTAREA") return;
      if (step >= 1 && step < TOTAL) {
        e.preventDefault();
        next();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, a]);

  const uploadFiles = async (list) => {
    for (const f of Array.from(list)) {
      setUploading((n) => n + 1);
      try {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/contact-files", fd);
        setFiles((prev) => [...prev, res.data]);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Impossible d'envoyer ${f.name}`);
      } finally {
        setUploading((n) => n - 1);
      }
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const res = await api.post("/prospects", {
        firstname: a.firstname.trim(),
        lastname: a.lastname.trim(),
        email: a.email.trim(),
        phone: a.phone.trim(),
        project_type: a.project_type,
        property_type: a.property_type,
        surface_range: a.surface_range,
        postal_code: a.postal_code.trim(),
        city: a.city.trim(),
        insee_code: a.insee_code || undefined,
        avancement: a.avancement,
        timeline: a.timeline,
        budget: a.budget,
        priorities: a.priorities,
        accompaniment: a.accompaniment,
        needs: a.needs,
        work_types: a.work_types,
        message: a.details,
        attachments: files,
        marketing_optin: a.marketing,
      });
      setRoutingCovered(res.data.covered);
      setStep(TOTAL + 1);
    } catch (e2) {
      setError("Une erreur est survenue lors de l'envoi. Réessayez dans un instant.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  /* ------------------------------ étapes ------------------------------ */

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div data-testid="estimate-step-1">
            <StepTitle>Quel est votre projet ?</StepTitle>
            <StepSub>Quelques informations suffisent pour commencer.</StepSub>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {PROJECT_TYPES.map((o, i) => (
                <ImageCard key={o.id} testid={`step1-option-${i}`} selected={a.project_type === o.id}
                  onClick={() => set("project_type", o.id)} img={o.img} title={o.id} sub={o.sub} />
              ))}
            </div>
          </div>
        );
      case 2:
        return (
          <div data-testid="estimate-step-2">
            <StepTitle>Parlez-nous du lieu</StepTitle>
            <p className="mt-8 text-sm font-bold text-navy">Quel type de lieu est concerné ?</p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {PROPERTY_TYPES.map((o, i) => (
                <LocationTypeCard key={o.id} testid={`step2-type-${i}`} selected={a.property_type === o.id}
                  onClick={() => set("property_type", o.id)} icon={o.icon} label={o.label} />
              ))}
            </div>
            <p className="mt-9 text-sm font-bold text-navy">Quelle est la surface approximative ?</p>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {SURFACES.map((s, i) => (
                <Pill key={s} testid={`step2-surface-${i}`} selected={a.surface_range === s}
                  onClick={() => set("surface_range", s)} label={s} />
              ))}
            </div>
            <p className="mt-3 text-xs italic text-stone-500">Une estimation suffit à ce stade.</p>
          </div>
        );
      case 3: {
        const options = WORK_OPTIONS[a.project_type] || WORK_OPTIONS["Rénovation"];
        return (
          <div data-testid="estimate-step-3">
            <StepTitle>Qu'avez-vous envie de réaliser ?</StepTitle>
            <StepSub>Plusieurs réponses possibles.</StepSub>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {options.map((w, i) => (
                <Pill key={w} testid={`step3-option-${i}`} selected={a.work_types.includes(w)}
                  onClick={() => toggleMulti("work_types", w)} label={w} />
              ))}
            </div>
            <p className="mt-9 text-sm font-bold text-navy">Envie de nous en dire plus ? <span className="font-normal text-stone-400">(facultatif)</span></p>
            <textarea
              data-testid="estimate-details"
              rows={4}
              value={a.details}
              onChange={(e) => set("details", e.target.value)}
              placeholder="Décrivez-nous votre projet en quelques mots…"
              className="mt-4 w-full rounded-2xl border-2 border-[#EDEAE3] bg-white px-5 py-4 text-base leading-relaxed text-navy placeholder:text-stone-400 focus:border-cognac focus:outline-none"
            />
            <p className="mt-8 text-sm font-bold text-navy">Vous avez déjà des photos, plans ou documents ? <span className="font-normal text-stone-400">(facultatif)</span></p>
            <div
              data-testid="estimate-upload-zone"
              className="mt-4 rounded-2xl border-2 border-dashed border-[#DDD8CE] bg-white px-6 py-8 text-center transition-colors duration-200 hover:border-cognac/50"
            >
              <input
                ref={fileRef}
                data-testid="estimate-upload-input"
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,application/pdf"
                className="hidden"
                onChange={(e) => uploadFiles(e.target.files)}
              />
              <Upload className="mx-auto h-6 w-6 text-sage" />
              <p className="mt-3 text-sm font-semibold text-navy">Photos, plans, devis existants, documents PDF</p>
              <p className="mt-1 text-xs text-stone-500">8 Mo maximum par fichier</p>
              <button
                type="button"
                data-testid="estimate-upload-button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading > 0}
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-navy/20 px-5 py-2.5 text-sm font-bold text-navy transition-colors duration-200 hover:border-cognac hover:text-cognac disabled:opacity-50"
              >
                {uploading > 0 ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading > 0 ? "Envoi en cours…" : "Ajouter des fichiers"}
              </button>
            </div>
            {files.length > 0 && (
              <ul className="mt-4 flex flex-col gap-2">
                {files.map((f, i) => (
                  <li key={f.path} data-testid={`estimate-file-${i}`}
                    className="flex items-center gap-3 rounded-xl border border-[#EDEAE3] bg-white px-4 py-3">
                    <FileText className="h-4 w-4 shrink-0 text-sage" />
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-navy">{f.name}</span>
                    <button type="button" data-testid={`estimate-file-remove-${i}`} aria-label={`Retirer ${f.name}`}
                      onClick={() => setFiles((prev) => prev.filter((x) => x.path !== f.path))}
                      className="rounded-full p-1.5 text-stone-400 transition-colors hover:bg-cognac/10 hover:text-cognac">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      }
      case 4:
        return (
          <div data-testid="estimate-step-4">
            <StepTitle>Où en êtes-vous aujourd'hui ?</StepTitle>
            <div className="mt-8 flex flex-col gap-3">
              {AVANCEMENTS.map((o, i) => (
                <OptionCard key={o.id} testid={`step4-option-${i}`} selected={a.avancement === o.id}
                  onClick={() => set("avancement", o.id)} title={o.id} sub={o.sub} />
              ))}
            </div>
          </div>
        );
      case 5:
        return (
          <div data-testid="estimate-step-5">
            <StepTitle>Quel budget envisagez-vous ?</StepTitle>
            <StepSub>
              Pas besoin d'avoir un budget parfaitement défini. Cette information nous aide
              simplement à mieux comprendre votre projet.
            </StepSub>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {BUDGETS.map((b, i) => (
                <Pill key={b} testid={`step5-option-${i}`} selected={a.budget === b}
                  onClick={() => set("budget", b)} label={b} />
              ))}
            </div>
          </div>
        );
      case 6:
        return (
          <div data-testid="estimate-step-6">
            <StepTitle>Quand aimeriez-vous commencer ?</StepTitle>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {TIMELINES.map((t, i) => (
                <Pill key={t} testid={`step6-option-${i}`} selected={a.timeline === t}
                  onClick={() => set("timeline", t)} label={t} />
              ))}
            </div>
          </div>
        );
      case 7:
        return (
          <div data-testid="estimate-step-7">
            <StepTitle>Où se situe votre projet ?</StepTitle>
            <StepSub>
              CozyHome se développe progressivement par secteurs. Ces informations nous
              permettent d'organiser l'accompagnement le plus adapté à votre projet.
            </StepSub>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="estimate-postal" className="text-sm font-bold text-navy">
                  Code postal <span className="text-cognac">*</span>
                </label>
                <input
                  id="estimate-postal"
                  data-testid="estimate-postal"
                  inputMode="numeric"
                  maxLength={5}
                  value={a.postal_code}
                  onChange={(e) => set("postal_code", e.target.value.replace(/\D/g, ""))}
                  placeholder="Ex. 70000"
                  className={`${inputClass} mt-3`}
                />
              </div>
              <div>
                <label htmlFor="estimate-city" className="text-sm font-bold text-navy">
                  Commune <span className="text-cognac">*</span>
                </label>
                {communeStatus === "multi" ? (
                  <select
                    id="estimate-city"
                    data-testid="estimate-city-select"
                    value={a.insee_code}
                    onChange={(e) => {
                      const m = communeMatches.find((x) => x.insee_code === e.target.value);
                      setA((prev) => ({ ...prev, city: m ? m.name : "", insee_code: e.target.value }));
                      setError("");
                    }}
                    className={`${inputClass} mt-3`}
                  >
                    <option value="">— Sélectionnez votre commune —</option>
                    {communeMatches.map((m) => (
                      <option key={m.insee_code} value={m.insee_code}>{m.name}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="estimate-city"
                    data-testid="estimate-city"
                    value={a.city}
                    onChange={(e) => set("city", e.target.value)}
                    placeholder="Ex. Vesoul"
                    autoComplete="address-level2"
                    readOnly={communeStatus === "single"}
                    className={`${inputClass} mt-3 ${communeStatus === "single" ? "bg-cream/60 text-slate-500" : ""}`}
                  />
                )}
                {communeStatus === "loading" && (
                  <p className="mt-2 text-xs text-slate-400">Recherche de la commune…</p>
                )}
                {communeStatus === "multi" && (
                  <p data-testid="estimate-city-multi-hint" className="mt-2 text-xs text-cognac">
                    Plusieurs communes partagent ce code postal : merci de préciser la vôtre.
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      case 8:
        return (
          <div data-testid="estimate-step-8">
            <StepTitle>Comment pouvons-nous vous aider ?</StepTitle>
            <StepSub>Plusieurs réponses possibles.</StepSub>
            <div className="mt-8 flex flex-col gap-3">
              {NEEDS.map((o, i) => (
                <OptionCard key={o.id} testid={`step8-option-${i}`} selected={a.needs.includes(o.id)}
                  onClick={() => toggleMulti("needs", o.id)} icon={o.icon} title={o.id} sub={o.sub} />
              ))}
            </div>
          </div>
        );
      case 9:
        return (
          <div data-testid="estimate-step-9">
            <Kicker>Dernière étape</Kicker>
            <StepTitle>Faisons connaissance</StepTitle>
            <StepSub>Laissez-nous vos coordonnées pour pouvoir échanger sur votre projet.</StepSub>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <input data-testid="estimate-firstname" value={a.firstname} onChange={(e) => set("firstname", e.target.value)}
                placeholder="Prénom" autoComplete="given-name" aria-label="Prénom" className={inputClass} />
              <input data-testid="estimate-lastname" value={a.lastname} onChange={(e) => set("lastname", e.target.value)}
                placeholder="Nom" autoComplete="family-name" aria-label="Nom" className={inputClass} />
              <input data-testid="estimate-email" type="email" value={a.email} onChange={(e) => set("email", e.target.value)}
                placeholder="Email" autoComplete="email" aria-label="Email" className={inputClass} />
              <input data-testid="estimate-phone" type="tel" value={a.phone} onChange={(e) => set("phone", e.target.value)}
                placeholder="Téléphone" autoComplete="tel" aria-label="Téléphone" className={inputClass} />
            </div>
            <label className="mt-7 flex cursor-pointer items-start gap-3 rounded-2xl border-2 border-[#EDEAE3] bg-white px-5 py-4">
              <input data-testid="estimate-consent" type="checkbox" checked={a.consent}
                onChange={(e) => set("consent", e.target.checked)}
                className="mt-0.5 h-5 w-5 shrink-0 accent-[#B57A51]" />
              <span className="text-sm leading-relaxed text-navy/80">
                J'accepte que mes informations soient utilisées pour traiter ma demande,
                conformément à la politique de confidentialité. <span className="text-cognac">*</span>
              </span>
            </label>
            <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-2xl border-2 border-[#EDEAE3] bg-white px-5 py-4">
              <input data-testid="estimate-marketing" type="checkbox" checked={a.marketing}
                onChange={(e) => set("marketing", e.target.checked)}
                className="mt-0.5 h-5 w-5 shrink-0 accent-[#B57A51]" />
              <span className="text-sm leading-relaxed text-stone-600">
                Je souhaite recevoir les conseils et actualités de CozyHome (facultatif).
              </span>
            </label>
          </div>
        );
      default:
        return null;
    }
  };

  /* ---------------------------- confirmation ---------------------------- */

  const summaryRows = [
    a.project_type && ["Type de projet", a.project_type],
    a.property_type && ["Type de bien", a.surface_range ? `${a.property_type} — ${a.surface_range}` : a.property_type],
    a.work_types.length > 0 && ["Travaux envisagés", a.work_types.join(", ")],
    (a.city.trim() || a.postal_code.trim()) && ["Localisation", [a.city.trim(), a.postal_code.trim()].filter(Boolean).join(" ")],
    a.budget && ["Budget", a.budget],
    a.timeline && ["Calendrier", a.timeline],
    a.needs.length > 0 && ["Besoin d'accompagnement", a.needs.join(", ")],
  ].filter(Boolean);

  /* ------------------------------ rendu ------------------------------ */

  return (
    <div data-testid="estimate-page" className="flex min-h-screen flex-col bg-ivory">
      <div ref={topRef} />

      {/* Header épuré */}
      <header className="sticky top-0 z-40 border-b border-[#EDEAE3] bg-[#FEFDF9]/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-5">
          <Link to="/" data-testid="estimate-logo" aria-label="Retour à l'accueil">
            <Logo className="h-7 w-auto" />
          </Link>
          <Link
            to="/"
            data-testid="estimate-quit"
            className="flex items-center gap-1.5 rounded-full border border-navy/15 px-4 py-2 text-xs font-bold text-navy/70 transition-colors duration-200 hover:border-cognac hover:text-cognac"
          >
            <X className="h-3.5 w-3.5" /> Quitter
          </Link>
        </div>
        {step >= 1 && step <= TOTAL && (
          <div>
            <div className="h-1 bg-navy/10">
              <motion.div
                data-testid="estimate-progress-bar"
                animate={{ width: `${(step / TOTAL) * 100}%` }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="h-full bg-cognac"
              />
            </div>
            <p data-testid="estimate-progress-label" className="mx-auto max-w-3xl px-5 pt-2 text-[11px] font-medium lowercase tracking-wide text-stone-400">
              étape {step} sur {TOTAL}
            </p>
          </div>
        )}
      </header>

      {/* Contenu */}
      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-5 pb-40 pt-6 sm:pt-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            className="my-auto"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            {step === 0 && (
              <div data-testid="estimate-intro" className="pt-6 sm:pt-12">
                <Kicker>Votre projet commence ici</Kicker>
                <StepTitle>Parlez-nous de votre projet.</StepTitle>
                <StepSub>
                  Rénovation, aménagement, extension ou construction : quelques informations
                  suffisent pour nous aider à comprendre votre projet et préparer la suite avec vous.
                </StepSub>
                <p className="mt-6 flex items-center gap-2 text-sm font-semibold text-sage">
                  <Clock className="h-4 w-4" /> Environ 3 minutes
                </p>
                <button
                  data-testid="estimate-start"
                  onClick={() => setStep(1)}
                  className="group mt-9 flex items-center gap-2.5 rounded-full bg-navy px-9 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-sage"
                >
                  Commencer
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <p className="mt-5 text-xs leading-relaxed text-stone-500">
                  Vos réponses nous permettront de mieux préparer notre premier échange.
                </p>
              </div>
            )}

            {step >= 1 && step <= TOTAL && renderStep()}

            {step === TOTAL + 1 && (
              <div data-testid="estimate-confirmation" className="pt-6 sm:pt-10">
                <motion.span
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="flex h-16 w-16 items-center justify-center rounded-full bg-sage/15 text-sage"
                >
                  <Check className="h-8 w-8" />
                </motion.span>
                <h1 className="mt-7 font-display text-3xl font-medium leading-tight tracking-tight text-navy sm:text-5xl">
                  Merci. Votre projet commence à <span className="italic text-sage">prendre forme.</span>
                </h1>
                <p className="mt-4 text-sm leading-relaxed text-stone-600 sm:text-base">
                  Nous avons bien reçu les premières informations concernant votre projet.
                  Elles vont nous permettre de mieux comprendre votre besoin et de préparer
                  la suite de votre accompagnement.
                </p>
                {routingCovered !== null && (
                  <p data-testid="estimate-routing-message" className="mt-4 rounded-2xl bg-sage/10 p-5 text-sm leading-relaxed text-navy sm:text-base">
                    {routingCovered ? COVERED_MESSAGE : UNCOVERED_MESSAGE}
                  </p>
                )}

                <div data-testid="confirmation-summary" className="mt-10 rounded-3xl border border-[#EDEAE3] bg-white p-6 sm:p-8">
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-sage">Votre projet en résumé</p>
                  <dl className="mt-5 space-y-3.5 text-sm">
                    {summaryRows.map(([k, v], i) => (
                      <div key={k} data-testid={`confirmation-row-${i}`} className="flex flex-col gap-1 border-b border-[#F1EEE7] pb-3.5 last:border-0 last:pb-0 sm:flex-row sm:justify-between sm:gap-6">
                        <dt className="shrink-0 text-stone-400">{k}</dt>
                        <dd className="font-semibold text-navy sm:text-right">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>

                <div className="mt-10">
                  <Link
                    data-testid="estimate-home"
                    to="/"
                    className="inline-flex items-center gap-2.5 rounded-full bg-navy px-8 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-sage"
                  >
                    Retour à l'accueil
                  </Link>
                </div>
              </div>
            )}

            {error && <ErrorMsg>{error}</ErrorMsg>}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Barre de navigation basse (accessible au pouce) */}
      {step >= 1 && step <= TOTAL && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[#EDEAE3] bg-[#FEFDF9]/95 backdrop-blur-md">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-5 py-4">
            <button
              data-testid="estimate-back"
              onClick={back}
              className="flex items-center gap-1.5 rounded-full px-5 py-3.5 text-sm font-bold text-navy/60 transition-colors duration-200 hover:text-cognac"
            >
              <ArrowLeft className="h-4 w-4" /> Retour
            </button>
            {step < TOTAL ? (
              <button
                data-testid="estimate-continue"
                onClick={next}
                className="flex items-center gap-2 rounded-full bg-navy px-8 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-sage"
              >
                Continuer <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                data-testid="estimate-submit"
                onClick={submit}
                disabled={submitting || uploading > 0}
                className="flex items-center gap-2 rounded-full bg-navy px-8 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-sage disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Envoyer mon projet
              </button>
            )}
          </div>
          {step === TOTAL && (
            <p className="mx-auto max-w-3xl px-5 pb-3 text-center text-[11px] leading-relaxed text-stone-500">
              Vos informations sont utilisées uniquement pour étudier votre demande et vous
              recontacter, conformément à notre politique de confidentialité.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
